import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DialogMode } from '../../../types/enum';
import { tap, catchError, of } from 'rxjs';
import { RecintoItemResultsViewModel, RecintoItemViewModel } from '../../../recintos/recinto.types';
import { FilterRecintosSolicitudCamViewModel, CultivosSolicitudCamViewModel, TwCultivoSolicitudCamViewModel } from '../solicitud-recinto-cam.types';
import { SolicitudCultivoCamService } from '../solicitud-cultivo-cam.service';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { FilterSearchingBaseViewModel } from 'src/app/components/core/_infrastructure/types/common';
import { TwSolicitudCamViewModel, TwSolicitudCamViewModelBase } from '../../../types/solicitudcam-datosbasicos.types';
import { FormGroup } from '@angular/forms';
import { CultivoViewModel } from '../../../cultivos/cultivo.types';

@Component({
    selector: 'app-solicitud-recinto-cam-wrapper-cultivos',
    templateUrl: './solicitud-recinto-cam-wrapper-cultivos.component.html',
    styleUrls: ['./solicitud-recinto-cam-wrapper-cultivos.component.scss'],
})
export class SolicitudRecintoCamWrapperCultivosComponent extends ComponentBase implements OnInit, OnChanges {
    @Input() recintoListMode: DialogMode;
    @Input() readonly: boolean;
    @Input() solicitudCam: TwSolicitudCamViewModel;
    @Input() formGroup: FormGroup;
    @Input() onSave$: EventEmitter<void>;
    @Input() onAdd$: EventEmitter<void>;

    @Output() saved = new EventEmitter<number>();
    @Output() numberOfChanges = new EventEmitter<number>(); // Llevamos la cuenta de cuántos elementos se han cambiado

    recintoCultivosSolicitud: RecintoItemResultsViewModel;
    cultivoListDialogVisible = false;
    payloadCultivosSolicitud: FilterRecintosSolicitudCamViewModel;
    dialogFilter: TwSolicitudCamViewModelBase;

    // Recinto PIA Detail view params
    recintoPIADetailVisible = false;
    recintoPIAIdSelected: number | null;

    private successMessage = 'Actualización realizada satisfactoriamente';
    private WarningMessageRecintosDuplicados = 'El Cultivo seleccionado ya estaba asociado a la solicitud y no se ha incluido de nuevo: ';
    private defaultOrdenColumn = 'Titular';

    constructor(
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private cultivoSolicitudCamService: SolicitudCultivoCamService,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.onSave$.subscribe(() => {
            this.onSaveCultivos();
        });
        this.onAdd$.subscribe(() => {
            this.onAddHandler();
        });
        this.form = this.formGroup;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.solicitudCam?.currentValue) {
            const solicitudCam = changes?.solicitudCam?.currentValue as TwSolicitudCamViewModel;
            if (solicitudCam) {
                this.dialogFilter = solicitudCam as TwSolicitudCamViewModelBase;
                this.initialLoadCultivosSolicitud(solicitudCam.Id!);
            }
        }
    }

    /**
     * Este método inicializa el payload que se utiliza para consultar los cultivos de la solicitud
     * e invoca al servicio de obtención de cultivos de la solicitud.
     * @param idSolicitudCam number Id. de la solicitud
     */
    private initialLoadCultivosSolicitud(idSolicitudCam: number): void {
        this.payloadCultivosSolicitud = new FilterRecintosSolicitudCamViewModel();
        this.payloadCultivosSolicitud.Asc = 0;
        this.payloadCultivosSolicitud.OrderBy = this.defaultOrdenColumn;
        this.payloadCultivosSolicitud.PageNumber = 0;
        this.payloadCultivosSolicitud.Id = idSolicitudCam;

        this.loadCultivos(this.payloadCultivosSolicitud);
    }

    private onAddHandler(): void {
        this.cultivoListDialogVisible = true;
    }

    private loadCultivos(payload: FilterRecintosSolicitudCamViewModel): void {
        this.cultivoSolicitudCamService
            .getCultivosSolicitudCam(payload)
            .pipe(
                tap((res: RecintoItemResultsViewModel) => {
                    this.recintoCultivosSolicitud = res;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * It will be invoked when using the paginator
     * @param lazyLoadParams FilterSearchingBaseViewModel
     * @return void
     */
    onSearchLazy(lazyLoadParams: FilterSearchingBaseViewModel): void {
        const payload = {
            ...this.payloadCultivosSolicitud,
            PageNumber: lazyLoadParams.PageNumber,
            Asc: lazyLoadParams.Asc,
            OrderBy: lazyLoadParams.OrderBy,
        } as FilterRecintosSolicitudCamViewModel;
        this.getRecintosPIASolicitudCam(payload);
    }

    /**
     * Este método obtiene la lista de recintos (o almacenes) de la solicitud indicada por parámetro.
     * @param payload FilterRecintosSolicitudCamViewModel
     * @returns void
     */
    private getRecintosPIASolicitudCam(payload: FilterRecintosSolicitudCamViewModel): void {
        this.payloadCultivosSolicitud = { ...payload } as FilterRecintosSolicitudCamViewModel;
        if (!payload) {
            return;
        }
        this.loadCultivos(this.payloadCultivosSolicitud);
    }

    onViewHandler(idRecintoPIA: number | null): void {
        this.recintoPIAIdSelected = idRecintoPIA;
        this.recintoPIADetailVisible = true;
    }

    /**
     * This method is executed after selecting Cultivos in the dialog.
     * @param selectedRecintosCultivos RecintoItemViewModel[]
     * @returns void
     */
    onSelectedCultivos(selectedRecintosCultivos: RecintoItemViewModel[]): void {
        selectedRecintosCultivos?.forEach(selectedRecintoCultivos => {
            // Se establece el IdUsuario para indicar que es un registro introducido por el usuario
            selectedRecintoCultivos.IdUsuario = this.loggedInUser?.Id || 0;
            this.addRecintoCultivos(selectedRecintoCultivos);
        });
        if (selectedRecintosCultivos?.length > 0) {
            this.updateListRecintos();
        }
        this.cultivoListDialogVisible = false;
    }

    /**
     * Este método intenta añadir los recintos-cultivos seleccionados a la lista ya existente de recintos-cultivos (la que traemos de BD).
     * Si el recinto-cultivo no existía se añade entero con todos sus cultivos seleccionados.
     * (@Revisión 2025/02/25: 1 cultivo por recinto).
     * Si el recinto ya existía, va añadiendo uno a uno los cultivos que no existieran previamente.
     * Además irá mostrando un mensaje por pantalla indicando que ha encontrado un cultivo coincidente (Se indica al usuario que ya estaba añadiddoo y no se vuelve a añadir).
     * @param recintoCultivosSelected RecintoItemViewModel
     * @returns void
     */
    private addRecintoCultivos(recintoCultivosSelected: RecintoItemViewModel): void {
        const recintoExistente = this.recintoCultivosSolicitud?.ResultList.find(item => item.Id === recintoCultivosSelected.Id); // Primero buscamos el recinto entre los ya añadidos (que eran los originales)

        if (!recintoExistente) {
            recintoCultivosSelected.TwCultivoCollection?.forEach(cultivo => {
                cultivo.IdUsuario = 0;
            });
            this.recintoCultivosSolicitud?.ResultList.push(recintoCultivosSelected);
            this.numberOfChanges.emit(recintoCultivosSelected.TwCultivoCollection?.length); // Tantos cambios como cultivos seleccionados
            return;
        }

        // Si encontramos el recinto, buscamos los cultivos seleccionados dentro de sus cultivos
        // Solo debería llegar un cultivo en la lista
        recintoCultivosSelected.TwCultivoCollection?.forEach(cultivoSelected => {
            const cultivoFound: CultivoViewModel | undefined = recintoExistente.TwCultivoCollection?.find(
                cultivoExistente => cultivoExistente.Id === cultivoSelected.Id,
            );
            if (cultivoFound === undefined) {
                if (!recintoExistente.TwCultivoCollection) {
                    recintoExistente.TwCultivoCollection = [];
                }
                cultivoSelected.IdUsuario = 0; // Ponemos un cero para controlar que es de los seleccionados por el usuario.
                recintoExistente.TwCultivoCollection?.push(cultivoSelected);
                this.numberOfChanges.emit(1);
                return;
            }
            this.addWarningMessage(
                this.WarningMessageRecintosDuplicados +
                    cultivoFound.Variedad +
                    ' - ' +
                    cultivoFound.NombreComun +
                    ' en el SigPac ' +
                    this.getSigpac(recintoExistente),
            );
        });
    }

    /**
     * Se recrea la lista de recintos.
     * Así forzamos el ngOnChanges en el componente recinto-list
     * @returns void
     */
    private updateListRecintos(): void {
        if (!this.recintoCultivosSolicitud) {
            return;
        }
        this.recintoCultivosSolicitud = { ...this.recintoCultivosSolicitud };
    }

    /**
     * Eliminar cultivo de la solicitud.
     * De los recintos-cultivos que hay asociados a la solicitud (que podrán haber sido añadidos desde la ventana de diálogo o venir de BD),
     * se marcan como "borrados".
     * @param cultivo CultivoViewModel
     * @return void
     */
    onDeleteCultivo(cultivo: CultivoViewModel): void {
        const recintoFound = this.recintoCultivosSolicitud?.ResultList?.find(value => value?.Id === cultivo.IdRecinto);
        if (!recintoFound) {
            return;
        }
        const cultivoFound = recintoFound.TwCultivoCollection?.find(cultivoFound => cultivoFound.Id === cultivo.Id);
        if (!cultivoFound) {
            return;
        }
        cultivoFound.MarkToDelete = 1;
        if (cultivoFound.IdUsuario) {
            this.numberOfChanges.emit(1);
        } else {
            this.numberOfChanges.emit(-1); // Borrar un cultivo que todavía no ha sido añadido, no implica cambios.
        }
        this.updateListRecintos(); // Así forzamos el ngOnChanges en el componente recinto-list
    }

    /**
     * Deshacer Eliminar cultivo de la solicitud.
     * Solo se pueden "deshacer" los recintos que han venido originalmente de la base de datos.
     * @param cultivo CultivoViewModel
     * @return void
     */
    onUndoDeleteCultivo(cultivo: CultivoViewModel): void {
        const recintoFound = this.recintoCultivosSolicitud?.ResultList.find(recinto => recinto.Id === cultivo.IdRecinto);
        if (!recintoFound) {
            return;
        }
        const cultivoFound = recintoFound.TwCultivoCollection?.find(cultivoFound => cultivoFound.Id === cultivo.Id);
        if (!cultivoFound) {
            return;
        }
        cultivoFound.MarkToDelete = 0;
        if (cultivoFound.IdUsuario) {
            this.numberOfChanges.emit(-1);
        } else {
            this.numberOfChanges.emit(1); // Volvemos a marcar como cambio, pues deshacemos el borrado de un cultivo seleccionado
        }

        this.updateListRecintos(); // Así forzamos el ngOnChanges en el componente recinto-list
    }

    /**
     * Se guardarán los cultivos (nuevos) de la solicitud. Se eliminarán los marcados a 1 en el campo MarkToDelete.
     * Se valida que cumplan con las limitaciones de variedad, CA y disponibilidad, para poder ser
     * añadidos a la solicitud en el periodo.
     */
    private onSaveCultivos(): void {
        const cultivosSolicitudCam = this.convertRecintosSolicitud(this.recintoCultivosSolicitud?.ResultList);
        if (cultivosSolicitudCam.length === 0) {
            this.saved.emit(-1);
            return;
        }
        const cultivosSolicitudCamVM: CultivosSolicitudCamViewModel = new CultivosSolicitudCamViewModel();
        cultivosSolicitudCamVM.IdCA = this.solicitudCam.IdCa;
        cultivosSolicitudCamVM.IdPeriodoCam = this.solicitudCam.IdPeriodoCam;
        cultivosSolicitudCamVM.cultivosSolicitudCam = cultivosSolicitudCam;

        this.cultivoSolicitudCamService
            .updateCultivosSolicitudCam(cultivosSolicitudCamVM)
            .pipe(
                tap(() => {
                    this.addSuccessMessage(this.successMessage);
                    this.saved.emit(this.payloadCultivosSolicitud.Id);
                    this.getRecintosPIASolicitudCam(this.payloadCultivosSolicitud);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    if (error?.error?.length > 0) {
                        this.addErrorMessage(error.error[0]?.message);
                    }
                    this.formGroup.disable();
                    this.saved.emit(0);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Para guardar los cultivos asociados a la solicitud, hay que convertir el array usado en el paginador,
     * en el array necesario para guardar los campos en los cultivos de la solicitud.
     * Los cultivos insertados por el usuario y borrados, no se incluirán en el array resultante.
     * (Los insertados por el usuario son los que no tienen IdUsuario. Los que vienen de BD si tienen IdUsuario).
     * Solo se incluirán aquellos cultivos originales que han sido "borrados" y los nuevos cultivos.
     * @param recintosCultivos RecintoItemViewModel[]
     * @returns TwRecintosPISolicitudCamViewModel[]
     */
    private convertRecintosSolicitud(recintosCultivos: RecintoItemViewModel[]): TwCultivoSolicitudCamViewModel[] {
        const recintosCultivosToBeSaved: TwCultivoSolicitudCamViewModel[] = [];
        recintosCultivos?.forEach(recintoCultivo => {
            recintoCultivo?.TwCultivoCollection?.forEach((cultivo: CultivoViewModel) => {
                const cultivoSolicitudViewModel = new TwCultivoSolicitudCamViewModel();
                cultivoSolicitudViewModel.IdCultivo = cultivo.Id;
                if (!cultivo.IdUsuario && cultivo.MarkToDelete) {
                    // Si el cultivo no es de los insertados mediante selección de recintos-cultivos (a los seleccionados se setea su IdUsuario a 0, para diferenciarlos)
                    // y tampoco ha sido marcado para borrar
                    // Se salta al siguiente recinto.
                    return;
                }
                if (cultivo.IdUsuario && !cultivo.MarkToDelete) {
                    // Tampoco se considera "cambio", un cultivo original que no hay sido marcado para borrar.
                    return;
                }
                cultivoSolicitudViewModel.IdUsuario = cultivo.IdUsuario;
                cultivoSolicitudViewModel.MarkToDelete = cultivo.MarkToDelete;
                cultivoSolicitudViewModel.IdSolicitudCam = this.payloadCultivosSolicitud.Id; // Es equivalente a: this.solicitudCam.Id
                cultivoSolicitudViewModel.FechaCreacion = undefined;
                recintosCultivosToBeSaved.push(cultivoSolicitudViewModel);
            });
        });

        return recintosCultivosToBeSaved;
    }

    /**
     * Se captura el evento que indica que la ventana de diálogo que muestra el detalle del recinto
     * se ha cerrado.
     * @param $event any
     */
    onRecintoDialogClosed($event: any): void {
        this.recintoPIADetailVisible = false;
    }

    private getSigpac(recinto: RecintoItemViewModel): string {
        return (
            recinto.IdProvincia +
            ':' +
            recinto.IdMunicipioSigPac +
            ':' +
            recinto.Agregado +
            ':' +
            recinto.Zona +
            ':' +
            recinto.Poligono +
            ':' +
            recinto.Parcela +
            ':' +
            recinto.Recinto
        );
    }
}
